import { createSlice } from '@reduxjs/toolkit';

/**
 * Approval requests — the durable approval queue (`/v5/:orgID/approval-requests`).
 *
 * NOT the Bookings → Approvals tab, which approves BOOKINGS directly through the booking state
 * machine. This is the engine behind `access_grant` and `visitor_access`, where a decision has a
 * claim, an apply contract, and outcomes that are not simply success or failure.
 *
 * 🛑 `lastOutcome` is a first-class piece of state, not a toast. Two of the server's outcomes
 * (`applyPending`, `applyStuck`) mean the outcome is UNKNOWN — a door may have opened. Those must
 * survive long enough for an operator to read and act on, so they are held here and cleared
 * deliberately rather than fading.
 */
const initialState = {
	items: [],
	loading: false,
	loaded: false,
	error: null,
	// The status filter the queue is currently showing. null = all.
	// 🛑 NO DEFAULT FILTER. The server's list is a strict filter, so defaulting to 'pending' meant
	// `items` could never contain a failed_apply row - and failed_apply sorting above pending is the
	// entire point of this screen. The rows most needing an operator would have been invisible until
	// someone thought to widen a filter they had no reason to touch, and the attention badge would
	// have read 0 while requests sat stranded.
	statusFilter: null,
	// Per-request in-flight guard, keyed by approvalRequestID, so one row's spinner does not
	// disable the whole table.
	deciding: {},
	// The classified result of the last decide/retry. See Redux/approvals/decideOutcome.js.
	lastOutcome: null
};

const approvalsSlice = createSlice({
	name: 'approvals',
	initialState,
	reducers: {
		loadApprovals: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadApprovalsSuccess: (state, action) => {
			state.loading = false;
			state.loaded = true;
			state.items = Array.isArray(action.payload) ? action.payload : [];
		},
		loadApprovalsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		setStatusFilter: (state, action) => {
			state.statusFilter = action.payload ?? null;
		},

		decideApproval: (state, action) => {
			state.deciding[action.payload.approvalRequestID] = true;
			state.error = null;
		},
		retryApproval: (state, action) => {
			state.deciding[action.payload.approvalRequestID] = true;
			state.error = null;
		},
		/**
		 * The single terminus for BOTH decide and retry, success or otherwise.
		 *
		 * ⚠️ There is no separate `…Error` action, deliberately. A refusal here is not an error: the
		 * server answered, and what it said is the payload. Splitting them would invite the UI to
		 * treat "the door is already granted" as a failure and "outcome unknown" as a success,
		 * which is the confusion the outcome classifier exists to prevent.
		 */
		decideApprovalSettled: (state, action) => {
			const { approvalRequestID, outcome } = action.payload;
			delete state.deciding[approvalRequestID];

			// 🛑 AN AMBIGUOUS OUTCOME IS STICKY. Decides on different rows run concurrently (takeEvery,
			// deliberately), so a second row settling a moment later would otherwise overwrite this one
			// slot - closing the "a door may have opened, do not retry" modal mid-read and replacing it
			// with a green toast for an unrelated success. The one message the design says must never
			// fade would be destroyed by the most ordinary event on the screen.
			//
			// So only an explicit dismissal (clearOutcome) clears an ambiguous outcome. A second
			// ambiguous outcome DOES replace it: both need reading, and dropping the newer one would
			// lose a warning entirely rather than merely delay it.
			if (state.lastOutcome?.severity === 'ambiguous' && outcome?.severity !== 'ambiguous') return;

			state.lastOutcome = outcome ? { ...outcome, approvalRequestID } : null;
		},
		clearOutcome: (state) => {
			state.lastOutcome = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// 🛑 ORG SWITCH MATTERS MORE HERE THAN ANYWHERE. Reset on switch START, not completion: an
		// approval queue is site- and org-scoped, and a stale row left on screen during the refetch
		// is a request from ANOTHER org sitting under Approve/Reject buttons. The window is small
		// and the action is irreversible, which is the wrong combination to leave to chance.
		builder.addCase('auth/switchProfile', () => initialState);
	}
});

export const approvalActions = approvalsSlice.actions;
export default approvalsSlice.reducer;
